import { Location } from 'history'
import { match, matchPath, RouteProps } from 'react-router'
import {
  actionUnavailableRouteProps,
  addExpenseRouteProps,
  bulkUploadExpenseLabelsRouteProps,
  CancelSubscriptionMatchParams,
  cancelSubscriptionRouteProps,
  changePasswordRouteProps,
  closeAccountRouteProps,
  closeBillPayCardRouteProps,
  closeVirtualCardRouteProps,
  createAutomationRuleRouteProps,
  createBillPayRouteProps,
  createBudgetRouteProps,
  createVirtualCardRouteProps,
  cspUserDetailRouteProps,
  DeactivateBudgetMatchParams,
  deactivateBudgetRouteProps,
  downloadTransactionsRouteProps,
  expensesTransactionsRouteProps,
  featureFlaggerRouteProps,
  helpRouteProps,
  invitePeopleRouteProps,
  ManageOrgIntegrationMatchParams,
  manageOrgIntegrationRouteProps,
  manageOrgInviteRouteProps,
  manageTrustedDevicesRouteProps,
  peopleCancelOrgInviteRouteProps,
  peopleInvitedToOrgDetailRouteProps,
  peopleInvitedToOrgRouteProps,
  peopleMembersDetailRouteProps,
  peopleMembersRouteProps,
  registerAccountRouteProps,
  registerOrganizationRouteProps,
  removeUserFromOrganizationRouteProps,
  requestBudgetRouteProps,
  requestVirtualCardRouteProps,
  screenVirtualCardRouteProps,
  searchRouteProps,
  setOrgExpenseReportCycleRouteProps,
  ShareBillPayVirtualCardMatchParams,
  shareBillPayVirtualCardRouteProps,
  transactionDetailRouteProps,
  updateAutomationRuleRouteProps,
  updateBillPayRouteProps,
  updateBudgetRequestRouteProps,
  updateBudgetRouteProps,
  updateBulkTransactionsRouteProps,
  updateExpenseRouteProps,
  updateProfilePhotoRouteProps,
  updateVirtualCardRouteProps,
  upgradeSubscriptionRouteProps,
  userScreeningInfoRouteProps,
} from 'src/navigation/routeProps'
import {
  budgetOverviewUrl,
  homeUrl,
  organizationInfoUrl,
  organizationIntegrationsUrl,
  peopleInvitedToOrgUrl,
  peopleMembersUrl,
  profileInfoUrl,
  profileSecurityUrl,
  signinUrl,
  virtualCardDetailUrl,
} from 'src/navigation/urls'

export interface BackgroundLocationStateBase<S> {
  backgroundLocation?: Location<S>
}

export interface NavigationLocationState
  extends BackgroundLocationStateBase<NavigationLocationState | undefined> {
  [key: string]: unknown
}

export type NavigationLocation = Location<NavigationLocationState | undefined>

export interface RoutePropsWithBackgroundLocation {
  routeProps: RouteProps
  exceptionRouteProps?: RouteProps[]
  // Accepts `Location<unknown>` (rather than the concrete `NavigationLocation`)
  // so any `Location<S>` background-location caller -- Redux selector or React
  // hook alike -- can call in without a cast; entries below only read
  // `location.pathname`, which every `Location<S>` has regardless of `S`, and
  // still return the concrete `NavigationLocation` shape.
  calculateDefaultBackgroundLocation: (
    location: Location<unknown>,
  ) => NavigationLocation
}

export const authedPopupRoutePropsWithBackgroundLocations: RoutePropsWithBackgroundLocation[] =
  [
    {
      routeProps: screenVirtualCardRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: deactivateBudgetRouteProps,
      calculateDefaultBackgroundLocation: (
        location: Location<unknown>,
      ): NavigationLocation => {
        const routeMatch = matchPath<DeactivateBudgetMatchParams>(
          location.pathname,
          deactivateBudgetRouteProps,
        ) as match<DeactivateBudgetMatchParams>
        return {
          pathname: budgetOverviewUrl(routeMatch.params.id),
          search: '',
          state: {},
          hash: '',
        }
      },
    },
    {
      routeProps: registerAccountRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: updateProfilePhotoRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: profileInfoUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: upgradeSubscriptionRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: userScreeningInfoRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: profileInfoUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: manageOrgIntegrationRouteProps,
      calculateDefaultBackgroundLocation: (
        location: Location<unknown>,
      ): NavigationLocation => {
        const routeMatch = matchPath<ManageOrgIntegrationMatchParams>(
          location.pathname,
          manageOrgIntegrationRouteProps,
        ) as match<ManageOrgIntegrationMatchParams>
        return {
          pathname: organizationIntegrationsUrl(routeMatch.params.id),
          search: '',
          state: {},
          hash: '',
        }
      },
    },
    {
      routeProps: actionUnavailableRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: downloadTransactionsRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: removeUserFromOrganizationRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: peopleMembersUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: closeVirtualCardRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => {
        return {
          pathname: homeUrl(),
          search: '',
          state: {},
          hash: '',
        }
      },
    },
    {
      routeProps: closeBillPayCardRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => {
        return {
          pathname: homeUrl(),
          search: '',
          state: {},
          hash: '',
        }
      },
    },
    {
      routeProps: closeAccountRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => {
        return {
          pathname: homeUrl(),
          search: '',
          state: {},
          hash: '',
        }
      },
    },
    {
      routeProps: setOrgExpenseReportCycleRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => {
        return {
          pathname: homeUrl(),
          search: '',
          state: {},
          hash: '',
        }
      },
    },
    {
      routeProps: shareBillPayVirtualCardRouteProps,
      calculateDefaultBackgroundLocation: (location): NavigationLocation => {
        const routeMatch = matchPath<ShareBillPayVirtualCardMatchParams>(
          location.pathname,
          shareBillPayVirtualCardRouteProps,
        )

        return {
          pathname: routeMatch?.params?.id
            ? virtualCardDetailUrl(routeMatch.params.id)
            : homeUrl(),
          search: '',
          state: {},
          hash: '',
        }
      },
    },
    {
      routeProps: cancelSubscriptionRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => {
        const routeMatch = matchPath<CancelSubscriptionMatchParams>(
          location.pathname,
          cancelSubscriptionRouteProps,
        ) as match<ManageOrgIntegrationMatchParams>

        return {
          pathname: organizationInfoUrl(routeMatch?.params?.id),
          search: '',
          state: {},
          hash: '',
        }
      },
    },
  ]

export const authedLeftFlyoutRoutePropsWithBackgroundLocations: RoutePropsWithBackgroundLocation[] =
  [
    {
      routeProps: helpRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: searchRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: cspUserDetailRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
  ]

export const authedRightFlyoutRoutePropsWithBackgroundLocations: RoutePropsWithBackgroundLocation[] =
  [
    {
      routeProps: createVirtualCardRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: requestVirtualCardRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: updateVirtualCardRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: createBudgetRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: requestBudgetRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: updateBudgetRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: updateBudgetRequestRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: transactionDetailRouteProps,
      exceptionRouteProps: [expensesTransactionsRouteProps],
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: invitePeopleRouteProps,
      exceptionRouteProps: [
        peopleMembersRouteProps,
        peopleInvitedToOrgRouteProps,
      ],
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: peopleMembersUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: peopleMembersDetailRouteProps,
      exceptionRouteProps: [peopleMembersRouteProps],
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: peopleMembersUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: peopleInvitedToOrgDetailRouteProps,
      exceptionRouteProps: [peopleInvitedToOrgRouteProps],
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: peopleInvitedToOrgUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: peopleCancelOrgInviteRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: peopleInvitedToOrgUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: registerOrganizationRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: manageOrgInviteRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: featureFlaggerRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: changePasswordRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: profileSecurityUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: manageTrustedDevicesRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: profileSecurityUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: addExpenseRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: updateExpenseRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: updateBulkTransactionsRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: createBillPayRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: updateBillPayRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: createAutomationRuleRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: updateAutomationRuleRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
    {
      routeProps: bulkUploadExpenseLabelsRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: homeUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
  ]

export const unauthedRightFlyoutRoutePropsWithBackgroundLocations: RoutePropsWithBackgroundLocation[] =
  [
    {
      routeProps: featureFlaggerRouteProps,
      calculateDefaultBackgroundLocation: (): NavigationLocation => ({
        pathname: signinUrl(),
        search: '',
        state: {},
        hash: '',
      }),
    },
  ]

export const authedFlyoutRoutePropsWithBackgroundLocations = [
  ...authedPopupRoutePropsWithBackgroundLocations,
  ...authedLeftFlyoutRoutePropsWithBackgroundLocations,
  ...authedRightFlyoutRoutePropsWithBackgroundLocations,
]
